import {Injectable, InjectFlags, InjectionToken, Injector, Type} from '@angular/core';

/**
 * Stores plugin providers which are normally only available in the child injector, which
 * loads cordova (via platform-loader), in the parent injector.
 */
@Injectable({
  providedIn: 'root',
})
export class εPluginInjector implements Injector {
  private readonly plugins = new Map<Type<unknown> | InjectionToken<unknown>, any>();

  public get<T>(token: Type<T> | InjectionToken<T>, notFoundValue?: T, flags?: InjectFlags): T;
  public get(token: any, notFoundValue?: any): any;
  public get(token: any, notFoundValue?: any, flags?: InjectFlags): any {
    const provider = this.plugins.get(token);

    if (provider != null) {
      return provider;
    }
    if (notFoundValue === undefined || notFoundValue === Injector.THROW_IF_NOT_FOUND) {
      throw new Error(`${stringify(token)} not found, did you load its module?`);
    }
    return notFoundValue;
  }

  public set<T>(token: Type<T> | InjectionToken<T>, value: T) {
    this.plugins.set(token, value);
  }
}

/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
function stringify(token: any): string {
  if (typeof token === 'string') {
    return token;
  }

  if (token instanceof Array) {
    return '[' + token.map(stringify).join(', ') + ']';
  }

  if (token == null) {
    return '' + token;
  }

  if (token.overriddenName) {
    return `${token.overriddenName}`;
  }

  if (token.name) {
    return `${token.name}`;
  }

  const res = token.toString();

  if (res == null) {
    return '' + res;
  }

  const newLineIndex = res.indexOf('\n');
  return newLineIndex === -1 ? res : res.substring(0, newLineIndex);
}
